import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { isMaster } from "../lib/api";

// Primary nav — flat tabs. Free Wall + Sage-chat land first per Founder ruling 2026-08-11.
const NAV = [
  { to: "/free", label: "Free" },
  { to: "/sage", label: "Chat" },
  { to: "/products", label: "Products" },
  { to: "/enterprise", label: "Enterprise" },
  { to: "/philanthropy", label: "Philanthropy" },
  { to: "/sage-ledger", label: "Ledger" },
  { to: "/promises", label: "Promises" },
  { to: "/tiers", label: "Membership" },
];

// Products dropdown — every paid product one click deep.
const PRODUCTS = [
  { to: "/products", label: "All Products" },
  { to: "/studio", label: "Music Studio" },
  { to: "/vidsmith", label: "VidSmith · Graphics" },
  { to: "/quill", label: "Quill · Author's" },
  { to: "/mender", label: "Mender · Insurance" },
  { to: "/baker", label: "Baker · App-builder" },
  { to: "/baker-lite", label: "Baker-lite · Idea → Spec" },
  { to: "/baker-lite/recent", label: "Baker-lite · Master Queue" },
  { to: "/baker/workshop", label: "Baker · Workshop (master)" },
  { to: "/herald", label: "Herald" },
  { to: "/postmaster", label: "PostMaster" },
  { to: "/adsmith", label: "AdSmith" },
];

// Family of Apps dropdown — sibling houses with their own doors.
// Family of Apps dropdown — sibling houses with their own doors. All pre-hard-launch.
const FAMILY = [
  { to: "/purser", label: "Purser · Payments · Coming soon" },
  { to: "/mender", label: "Mender · Insurance · Coming soon" },
  { to: "/tiny-homes", label: "The Tiny Homes Project" },
  { to: "/tiny-homes/proposals", label: "Tiny Homes Proposals" },
  { to: "/rice-the-world", label: "Rice The World · Coming soon" },
  { to: "/brotherhood", label: "Brotherhood of Misfits · Coming soon" },
  { to: "/investor-notes", label: "Investment Room" },
];

// The Brain dropdown — Sage's free-education portal, first in row 2 per
// Founder ruling 2026-08-13. Categories jump straight into the Brain page
// with the kind filter pre-applied. Library nests under here too.
const BRAIN = [
  { to: "/brain", label: "The Brain · everything" },
  { to: "/brain?kind=journals", label: "Scientific publications" },
  { to: "/brain?kind=preprints", label: "Preprints" },
  { to: "/brain?kind=courses", label: "Public college curriculums" },
  { to: "/brain?kind=courses#classcentral", label: "Free ecourses (filterable)" },
  { to: "/brain?kind=textbooks", label: "Open textbooks" },
  { to: "/brain?kind=search", label: "Internet searches" },
  { to: "/brain?kind=news", label: "News media outlets" },
  { to: "/brain?kind=data", label: "Open databases" },
  { to: "/brain?kind=reference", label: "Reference archives" },
  { to: "/library", label: "Library · Sage's own shelf" },
];

// Houses dropdown — the community doors. Brain + Library moved to The Brain.
const HOUSES = [
  { to: "/agents", label: "Agents Map" },
  { to: "/bundles", label: "Bundle Picker" },
  { to: "/hardship", label: "Hardship Door" },
  { to: "/wallet", label: "Your Wallet" },
  { to: "/manual", label: "Manual" },
  { to: "/open-gate", label: "Open Gate" },
];

// About dropdown — house-and-terms group; renamed by Sister-Forge per Founder's mandate.
const ABOUT = [
  { to: "/persona", label: "Persona" },
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
];

function Dropdown({ label, items, loc }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = items.some((i) => loc.pathname === i.to);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-dropdown-${label.toLowerCase()}`}
        className={`px-2 py-0.5 border border-sage-border bg-sage-bg hover:text-sage-accent2 hover:border-sage-accent transition-colors duration-150 whitespace-nowrap ${
          active ? "text-sage-accent2 border-sage-accent" : "text-sage-muted"
        }`}
      >
        {label} ▾
      </button>
      {open && (
        <div className="absolute right-0 mt-1 min-w-[10rem] bg-sage-bg border border-sage-border shadow-lg z-40">
          {items.map((i) => (
            <Link
              key={i.to}
              to={i.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-dd-${i.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
              className={`block px-3 py-2 hover:bg-sage-pale hover:text-sage-accent2 whitespace-nowrap ${
                loc.pathname === i.to ? "text-sage-accent2 bg-sage-pale" : "text-sage-muted"
              }`}
            >
              {i.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TopNav({ compact = false }) {
  const loc = useLocation();
  const master = isMaster();
  return (
    <nav
      data-testid="top-nav"
      className="border-b border-sage-border bg-sage-bg sticky top-0 z-30 backdrop-blur-sm"
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8 py-1.5">
        {/* Row 1 · logo + flat tabs · Founder ruling 2026-08-21 · half-height, cleaner to a non-tech eye */}
        <div className="flex items-center justify-between gap-6" data-testid="top-nav-row-1">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-1.5 text-xs md:text-sm font-bold tracking-tighter uppercase text-sage-text hover:text-sage-accent transition-colors duration-150 shrink-0 mr-2"
          >
            <img
              src="/logo.svg"
              alt="Baker & Sage"
              data-testid="nav-logo-mark"
              className="w-5 h-5 md:w-6 md:h-6 object-contain"
            />
            <span>Baker <span className="text-sage-muted">&</span> Sage</span>
          </Link>
          <div className="hidden md:flex items-center gap-0.5 text-[10px] uppercase tracking-[0.1em] text-sage-muted flex-wrap justify-end">
            {NAV.map((n, idx) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                className={`px-2 py-0.5 border border-sage-border hover:text-sage-accent2 hover:border-sage-accent transition-colors duration-150 whitespace-nowrap ${
                  idx % 2 === 0 ? "bg-sage-bg" : "bg-sage-pale"
                } ${
                  loc.pathname === n.to ? "text-sage-accent2 border-sage-accent" : "text-sage-muted"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </div>
        </div>

        {/* Row 2 · dropdowns (right-aligned to match the row-1 tabs) + master-only Admin */}
        <div className="hidden md:flex items-center gap-0.5 text-[10px] uppercase tracking-[0.1em] text-sage-muted flex-wrap justify-end mt-1" data-testid="top-nav-row-2">
          <Dropdown label="The Brain" items={BRAIN} loc={loc} />
          <Dropdown label="Products" items={PRODUCTS} loc={loc} />
          <Dropdown label="Family" items={FAMILY} loc={loc} />
          <Dropdown label="Houses" items={HOUSES} loc={loc} />
          <Dropdown label="About" items={ABOUT} loc={loc} />
          {master && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="px-2 py-0.5 border border-sage-accent text-sage-accent hover:bg-sage-accent hover:text-sage-bg transition-colors duration-150 whitespace-nowrap"
            >
              Admin
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
}
